import { isNil } from 'lodash';
import styled, {
    css
} from 'styled-components';

export const applyLeftPixelSpacing = (pixels = 0) => css`margin-left: ${pixels}px;`;

export const applyRightPixelSpacing = (pixels = 0) => css`margin-right: ${pixels}px;`;

export const applyTopPixelSpacing = (pixels = 0) => css`margin-top: ${pixels}px;`;

export const applyBottomPixelSpacing = (pixels = 0) => css`margin-bottom: ${pixels}px;`;

export interface HorizontalSpacing {
    horizontalSpacing?: number;
    leftSpacing?: number;
    rightSpacing?: number
}

export const applyOptionalSpacing = (applySpacing: (spacing: number) => ReturnType<typeof css>, spacing?: number) => {
    const shouldApplySpacing = !isNil(spacing) && isFinite(spacing);

    if (!shouldApplySpacing) {
        return '';
    }

    return applySpacing(spacing!);
}

/**
 * Apply horizontal margins either as separate left and right or together.
 * Specific sides have priority over united widths.
 * @param horizontalSpacing
 * @param leftSpacing
 * @param rightSpacing
 */
export const applyHorizontalSpacing = (
    {
        horizontalSpacing,
        leftSpacing,
        rightSpacing
    }: HorizontalSpacing
) => {
    return css`
      ${applyOptionalSpacing(applyLeftPixelSpacing, leftSpacing ?? horizontalSpacing)};
      ${applyOptionalSpacing(applyRightPixelSpacing, rightSpacing ?? horizontalSpacing)};
    `;
};

type InnerSpacingDirection = 'top' | 'left';
/**
 * Applies inner spacing to adjacent elements from the first adjacent element onwards (ie. 2nd element onwards)
 * @example
 * export const MyDiv = styled.div`
 *  ${applyInnerSpacing(10)};
 * `;
 *
 * <MyDiv>
 *     <Child1 />
 *     <Child2 />
 *     <Child3 />
 * </MyDiv>
 *
 * // means that Child2 and Child3 will be affected by this rule
 *
 * @param direction
 * @param innerSpacing - Will be treated as px values if given as a number
 */
export const applyInnerSpacing = (direction: InnerSpacingDirection, innerSpacing: number | string) => {
    const spacing = typeof innerSpacing === 'number' ? `${innerSpacing}px` : innerSpacing;
    return css`
        & > * + * {
          margin-${direction}: ${spacing};
        }
    `;
};

/**
 * Applies graded inner spacing in a chosen direction.
 * @param direction
 * @param innerSpacingGrades
 * @param fallbackGrade
 * @param grade - Optionally supplied grade to apply. If absent, the fallbackGrade is used.
 */
export function applyGradedInnerSpacing<T extends string>(
    direction: InnerSpacingDirection,
    innerSpacingGrades: Record<T, string | number>,
    fallbackGrade: T,
    grade?: T
) {
    const spacingGrade = !isNil(grade) ? innerSpacingGrades[grade] : innerSpacingGrades[fallbackGrade];
    return applyInnerSpacing(direction, spacingGrade);
}

export type SpacingGrade = 'none' | 'small' | 'medium' | 'large';
// possibly a good candidate to get from theme provider
// treated as pixels
export const getSpacingGrades: () => Record<SpacingGrade, number> = () => ({
    none: 0,
    small: 10,
    medium: 20,
    large: 30
});

export interface VerticalLayoutProps {
    innerSpacing?: SpacingGrade;
    bottomSpacing?: SpacingGrade;
}
/**
 * Adds vertical spacing between all the children depending on the grade.
 * Defaults to 'small' spacing.
 * Does not apply flex vertically and relies on block displayed child elements.
 */
export const VerticalLayout = styled.div<VerticalLayoutProps>`
  ${({ innerSpacing }) => applyGradedInnerSpacing('top', getSpacingGrades(), 'small', innerSpacing)};
  ${({ bottomSpacing }) => {
    if (isNil(bottomSpacing)) {
        return '';
    }
    
    const bottomSpacingGrade = getSpacingGrades()[bottomSpacing!];
    return applyOptionalSpacing(applyBottomPixelSpacing, bottomSpacingGrade);
  }};
`;

export interface HorizontalLayoutProps {
    innerSpacing?: SpacingGrade;
}
/**
 * Adds horizontal spacing between all the children depending on the grade.
 * Defaults to 'small' spacing.
 * Does not apply flex horizontally and relies on block displayed child elements.
 */
export const HorizontalLayout = styled.div<HorizontalLayoutProps>`
  ${({ innerSpacing }) => applyGradedInnerSpacing('left', getSpacingGrades(), 'small', innerSpacing)};
`;



// WEBPACK FOOTER //
// ./src/styles/spacing-styles.tsx